import { FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Header from 'components/GlobalStyles/Header'
import RoundedButton from 'components/RoundedButton'
import StarIcon from 'components/SVG/Star'
import Spacer from 'components/Spacer'

import { addCommas } from 'utils/stringFormatting'

import { TestimonialData } from 'data/MarketingData/TestimonialData'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${props => props.theme.metrics.contentMaxWidth}px;
`

interface IGridProps {
  cols?: number
  noPadding?: boolean
}
const IconGridContainer = styled.div<IGridProps>`
  /* modern browser progressive enhancement */
  @supports (display: grid) {
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 25px;
    margin: 0 ${props => (props.noPadding ? 0 : props.theme.metrics.gutterPhone)}px;
    @media (min-width: ${props => props.theme.metrics.tablet}px) {
      grid-row-gap: 30px;
      grid-template-columns: 1fr;
      margin: 0;
    }
    @media (min-width: ${props => props.theme.metrics.desktop}px) {
      grid-row-gap: 50px;
      grid-template-columns: repeat(${props => props.cols || 2}, 1fr);
      grid-column-gap: 80px;
    }
  }
`

const TestimonialContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
    border: none;
    padding: 0;
    max-width: 800px;
  }
`

const PersonIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    align-items: flex-start;
  }
`

const PersonIcon = styled.img`
  height: 120px;
  width: 120px;
  border-radius: 50%;
`

const RatingContainer = styled.div`
  svg {
    margin: 0 2px;
  }
`

const CustomerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    justify-content: flex-start;
  }
`

interface ICustomerTextProps {
  color?: string
  italic?: boolean
}
const CustomerText = styled.div<ICustomerTextProps>`
  font-style: ${props => (props.italic ? 'italic' : 'normal')};
  font-weight: 400;
  font-size: 14px;
  line-height: 25px;
  color: ${props => (props.color ? props.color : 'black')};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    justify-content: flex-start;
  }
`

const BoldText = styled.span`
  font-weight: 800;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

interface IProps {
  campaignType: string
  stateCampaign?: boolean | undefined
}

const Testimonials: FunctionComponent<IProps> = ({ campaignType, stateCampaign }) => {
  const theme = useContext(ThemeContext)

  const testimonials = TestimonialData.filter(testimonial =>
    Array.isArray(testimonial.campaignType)
      ? testimonial.campaignType.indexOf(campaignType) > -1
      : testimonial.campaignType === campaignType,
  )

  return (
    <Container>
      <Header
        text="See What Our Customers are Saying"
        fontSize={24}
        fontTabletSize={32}
        fontDesktopSize={42}
        headerThree
      />
      <Spacer
        size={50}
        tabletSize={30}
        responsiveSize={20}
      />
      <IconGridContainer cols={stateCampaign ? 1 : 2}>
        {testimonials.map(testimonial => {
          const reviews = testimonial.review.split(testimonial.highlight)
          return (
            <TestimonialContainer key={`${testimonial.name}-testimonial`}>
              {!stateCampaign && (
                <PersonIconContainer>
                  <PersonIcon src={testimonial.imgUrl} />
                </PersonIconContainer>
              )}
              <Spacer size={22} />
              <CustomerTextContainer>
                <RatingContainer>
                  <StarIcon
                    fill={theme.colors.yellow2}
                    width={20}
                    height={20}
                  />
                  <StarIcon
                    fill={theme.colors.yellow2}
                    width={20}
                    height={20}
                  />
                  <StarIcon
                    fill={theme.colors.yellow2}
                    width={20}
                    height={20}
                  />
                  <StarIcon
                    fill={theme.colors.yellow2}
                    width={20}
                    height={20}
                  />
                  <StarIcon
                    fill={theme.colors.yellow2}
                    width={20}
                    height={20}
                  />
                </RatingContainer>
                <Spacer size={5} />
                <CustomerText>
                  {reviews[0]}
                  <BoldText>{testimonial.highlight}</BoldText>
                  {reviews[1]}
                </CustomerText>
                <CustomerText color={theme.colors.transparentBlackAlpha50}>
                  {stateCampaign
                    ? `- ${testimonial.name}, sold their home ${
                      testimonial.location ? `in ${testimonial.location}` : ''
                    } and saved $${addCommas(testimonial.savings)} of their equity.`
                    : `- ${testimonial.name}, sold their home and saved $${addCommas(
                      testimonial.savings,
                    )} of their equity.`}
                </CustomerText>
              </CustomerTextContainer>
            </TestimonialContainer>
          )
        })}
      </IconGridContainer>
      <Spacer
        size={40}
        responsiveSize={20}
      />
      <ButtonContainer>
        <RoundedButton
          href="https://www.zillow.com/profile/HomeLister/#reviews"
          text="See more reviews"
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          color={theme.colors.white}
          fontSize={16}
          radius={50}
          height={50}
          width={265}
          letterSpacing="1px"
          externalLink
        />
        <Spacer size={20} />
        <RoundedButton
          href="/listings/sold"
          text="View all sold homes"
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          color={theme.colors.white}
          fontSize={16}
          radius={50}
          height={50}
          width={265}
          letterSpacing="1px"
        />
      </ButtonContainer>
    </Container>
  )
}

export default Testimonials
